import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { API_URI } from "../../helpers";
import { TIERRA } from "./mundo";

/**
 * Tablero en tiempo real: dónde está pasando la actividad ahora, de qué campaña viene
 * y cómo va el embudo.
 *
 * Sobre el mapa: es un mapa de PUNTOS sobre proyección equirectangular, no un
 * coropleto. El panel es un CRA viejo (React 16.9 / webpack 4) y meterle una librería
 * de mapas con su topojson era más riesgo que valor; las coordenadas ya las devuelve
 * geoip, así que los puntos salen gratis. Los números finos están en la tabla de al
 * lado, que es lo que se lee de verdad.
 */

// Proyección equirectangular recortada: casi todo el tráfico es América + Europa, así
// los puntos no quedan apretados en una esquina. Fuera de rango se clampea al borde.
// Apaisado a propósito: es la pieza central del tablero, debajo de los indicadores.
const MAPA = { lonMin: -125, lonMax: 45, latMin: -56, latMax: 62, w: 1000, h: 400 };

const proyectar = (lat, lon) => {
  const x = ((Math.min(MAPA.lonMax, Math.max(MAPA.lonMin, lon)) - MAPA.lonMin) / (MAPA.lonMax - MAPA.lonMin)) * MAPA.w;
  const y = ((MAPA.latMax - Math.min(MAPA.latMax, Math.max(MAPA.latMin, lat))) / (MAPA.latMax - MAPA.latMin)) * MAPA.h;
  return { x, y };
};

/**
 * Contorno de la tierra en un solo path (151 subtrazos, 2438 puntos). Se arma una vez al
 * cargar el módulo: proyectar 2438 puntos es nada y así el render no lo repite.
 *
 * Los datos ya vienen recortados a la ventana de MAPA (ver scripts/gen-mundo.py), así que
 * el clamp de proyectar() no llega a deformar nada. Si se cambia la ventana del mapa hay
 * que regenerar mundo.js, o las costas van a quedar estiradas contra el borde.
 */
const TIERRA_PATH = TIERRA.map((plano) => {
  let d = "";
  for (let i = 0; i < plano.length; i += 2) {
    const { x, y } = proyectar(plano[i + 1], plano[i]);
    d += (i === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1);
  }
  return d + "Z";
}).join("");

const VENTANAS = [
  { v: 15, l: "15 min" },
  { v: 60, l: "1 hora" },
  { v: 240, l: "4 horas" },
  { v: 1440, l: "24 horas" },
];

/**
 * Filtro de audiencia. El alumno se detecta porque su navegador pide /student/get con el
 * token en cada carga; el filtro saca (o deja) las claves de visitante que dieron esa
 * señal en la ventana. Sólo afecta a lo que sale de activity_events — ventas y consultas
 * no se filtran, y el pie lo aclara.
 */
const AUDIENCIAS = [
  { v: "todos",      l: "todos",        t: "Alumnos y visitantes sin cuenta, todo junto" },
  { v: "no_alumnos", l: "sin alumnos",  t: "Sólo quienes NO se identificaron como alumnos: el movimiento de venta" },
  { v: "alumnos",    l: "sólo alumnos", t: "Sólo quienes están logueados como alumnos" },
];

const ISO_FLAG = (iso) => {
  if (!iso || iso.length !== 2) return "";
  try {
    return String.fromCodePoint(...iso.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0)));
  } catch (e) {
    return "";
  }
};

const nf = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("es-AR"));

const useStyles = makeStyles({
  root: { padding: "0 6px 24px", fontFamily: '-apple-system, "Segoe UI", Roboto, sans-serif', color: "#0F172A" },
  bar: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 },
  title: { fontSize: 20, fontWeight: 800, marginRight: "auto" },
  chip: {
    border: "1px solid #CBD5E1", background: "#fff", borderRadius: 999, padding: "5px 12px",
    fontSize: 13, cursor: "pointer", fontWeight: 600, color: "#475569",
  },
  chipOn: { background: "#4338CA", borderColor: "#4338CA", color: "#fff" },
  // La audiencia se marca en verde para que no se confunda con la ventana de tiempo:
  // son dos filtros distintos y se ven uno al lado del otro.
  chipAud: { background: "#0D9488", borderColor: "#0D9488", color: "#fff" },
  separador: { width: 1, alignSelf: "stretch", background: "#E2E8F0", margin: "0 4px" },
  pulse: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#64748B" },
  dot: { width: 8, height: 8, borderRadius: "50%", background: "#22C55E" },
  dotOff: { background: "#EF4444" },
  banner: {
    borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, lineHeight: 1.5,
    background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A",
  },
  // 132px: con 8 tarjetas, 150 hacía que la última se cayera sola a una segunda fila.
  cards: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))", gap: 10, marginBottom: 16 },
  card: { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "12px 14px" },
  cardLbl: { fontSize: 11, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4 },
  cardVal: { fontSize: 26, fontWeight: 800, marginTop: 2, lineHeight: 1.1 },
  cardSub: { fontSize: 11.5, color: "#64748B", marginTop: 2 },
  up: { color: "#16A34A", fontWeight: 700 },
  down: { color: "#DC2626", fontWeight: 700 },
  grid2: { display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(0, 1fr)", gap: 14, marginBottom: 16 },
  // El mapa ocupa todo el ancho; la tabla por país baja al bloque de paneles.
  mapaPanel: { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 14, marginBottom: 16, position: "relative", overflow: "hidden" },
  lienzo: { position: "relative", width: "100%" },
  capaGlobos: { position: "absolute", inset: 0, pointerEvents: "none" },
  burbuja: {
    position: "absolute", transform: "translate(-50%, -100%)", whiteSpace: "nowrap",
    background: "rgba(255,255,255,0.97)", border: "1px solid #E2E8F0", borderRadius: 999,
    padding: "3px 9px", fontSize: 11.5, fontWeight: 700, color: "#334155",
    boxShadow: "0 4px 14px rgba(15,23,42,0.14)", animation: "$flotar 6s ease-out forwards",
  },
  "@keyframes flotar": {
    "0%":   { opacity: 0, transform: "translate(-50%, -60%) scale(0.85)" },
    "12%":  { opacity: 1, transform: "translate(-50%, -100%) scale(1)" },
    "75%":  { opacity: 1, transform: "translate(-50%, -180%) scale(1)" },
    "100%": { opacity: 0, transform: "translate(-50%, -230%) scale(0.95)" },
  },
  carril: {
    position: "absolute", left: 12, bottom: 10, display: "flex", flexDirection: "column-reverse",
    gap: 5, pointerEvents: "none", maxHeight: "62%", overflow: "hidden",
  },
  chipCarril: {
    background: "rgba(255,255,255,0.97)", border: "1px solid #E2E8F0", borderRadius: 999,
    padding: "3px 9px", fontSize: 11.5, fontWeight: 700, color: "#334155",
    boxShadow: "0 4px 14px rgba(15,23,42,0.12)", animation: "$flotarCarril 6s ease-out forwards",
  },
  "@keyframes flotarCarril": {
    "0%":   { opacity: 0, transform: "translateX(-14px)" },
    "12%":  { opacity: 1, transform: "translateX(0)" },
    "78%":  { opacity: 1, transform: "translateX(0)" },
    "100%": { opacity: 0, transform: "translateY(-10px)" },
  },
  // ── Cinta de actividad ─────────────────────────────────────
  // Va SUPERPUESTA sobre el borde derecho del mapa, con transparencia y blur, para no
  // robarle ancho al mapa. Tapa la zona de Europa/África, que es la de menos tráfico, y
  // se puede plegar. En pantallas angostas se cae abajo del mapa como un bloque normal.
  cinta: {
    position: "absolute", top: 0, right: 0, bottom: 0, width: 302, zIndex: 3,
    display: "flex", flexDirection: "column",
    background: "rgba(255,255,255,0.74)",
    backdropFilter: "blur(7px)", WebkitBackdropFilter: "blur(7px)",
    borderLeft: "1px solid rgba(203,213,225,0.85)",
    borderRadius: "0 10px 10px 0",
    "@media (max-width: 1100px)": {
      position: "static", width: "auto", height: 260, marginTop: 10,
      border: "1px solid #E2E8F0", borderRadius: 10, background: "#fff",
      backdropFilter: "none", WebkitBackdropFilter: "none",
    },
  },
  cintaCab: {
    display: "flex", alignItems: "center", gap: 6, padding: "7px 10px",
    borderBottom: "1px solid rgba(203,213,225,0.7)", fontSize: 11,
    fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4,
  },
  cintaLista: {
    flex: 1, overflowY: "auto", overflowX: "hidden", padding: "6px 8px 8px",
    display: "flex", flexDirection: "column", gap: 4,
    // El scroll propio no tiene que arrastrar la página cuando llega al final.
    overscrollBehavior: "contain",
  },
  cintaFila: {
    display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 8px",
    background: "rgba(255,255,255,0.92)", border: "1px solid #EEF2F7",
    borderLeft: "4px solid #CBD5E1", borderRadius: 8, fontSize: 12, lineHeight: 1.35,
    flexShrink: 0,
  },
  cintaFilaNueva: { animation: "$aparecerFila 0.5s cubic-bezier(.2,1.2,.3,1)" },
  cintaFilaVenta: { background: "rgba(220,252,231,0.95)", borderColor: "#BBF7D0" },
  cintaClickeable: { cursor: "pointer", "&:hover": { background: "#fff", borderColor: "#CBD5E1" } },
  // El ícono es lo que se lee primero en la cinta, así que va en su propia pastilla y
  // grande: a 12 px se perdía y había que leer el texto para saber de qué era la fila.
  cintaIcono: {
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    width: 24, height: 24, borderRadius: 8, fontSize: 14, fontWeight: 800, lineHeight: 1,
  },
  // marginRight además del gap: la banderita del título arranca sin espacio propio y la
  // hora le quedaba pegada.
  cintaHora: { fontSize: 10.5, color: "#94A3B8", fontVariantNumeric: "tabular-nums", flexShrink: 0, lineHeight: 1.5, marginRight: 2 },
  // display:block en título y detalle: sin eso el ellipsis no corta (necesita un bloque
  // con ancho) y las dos líneas se pegarían en una sola.
  cintaTexto: { minWidth: 0, flex: 1 },
  cintaTitulo: { display: "block", fontWeight: 700, color: "#1E293B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  cintaDetalle: { display: "block", fontSize: 11, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  cintaVeces: {
    marginLeft: 5, background: "#EEF2FF", color: "#4338CA", borderRadius: 999,
    padding: "0 5px", fontSize: 10, fontWeight: 800,
  },
  "@keyframes aparecerFila": {
    "0%":   { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  cintaBoton: {
    border: "1px solid #CBD5E1", background: "rgba(255,255,255,0.9)", borderRadius: 999,
    fontSize: 10.5, fontWeight: 700, color: "#475569", cursor: "pointer", padding: "2px 8px",
  },
  cintaAbajo: {
    position: "absolute", left: 0, right: 0, bottom: 8, margin: "0 auto", width: "fit-content",
    background: "#4338CA", color: "#fff", border: 0, borderRadius: 999, padding: "4px 12px",
    fontSize: 11, fontWeight: 700, cursor: "pointer", zIndex: 4,
    boxShadow: "0 6px 18px rgba(67,56,202,0.35)",
  },
  cintaPlegada: {
    position: "absolute", top: 8, right: 8, zIndex: 3,
    border: "1px solid #CBD5E1", background: "rgba(255,255,255,0.92)", borderRadius: 999,
    fontSize: 11, fontWeight: 700, color: "#475569", cursor: "pointer", padding: "4px 10px",
  },

  leyenda: { display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11.5, color: "#64748B", marginTop: 8 },
  // marginRight además del gap: con los emojis de por medio los ítems quedaban pegados.
  puntoLeyenda: { display: "inline-flex", alignItems: "center", gap: 5, marginRight: 8 },
  grid3: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 },
  panel: { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 14, minWidth: 0 },
  panelTitle: { fontSize: 13, fontWeight: 800, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 },
  hint: { fontSize: 11.5, color: "#94A3B8", fontWeight: 400 },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: { textAlign: "left", color: "#64748B", fontWeight: 700, padding: "5px 6px", borderBottom: "1px solid #E2E8F0", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3 },
  td: { padding: "6px", borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  barra: { height: 6, borderRadius: 999, background: "#4338CA" },
  vacio: { fontSize: 13, color: "#94A3B8", padding: "14px 4px", lineHeight: 1.6 },
  feed: { maxHeight: 260, overflowY: "auto", fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  feedRow: { display: "flex", gap: 8, padding: "3px 0", borderBottom: "1px solid #F8FAFC", color: "#475569" },

  // ── Animaciones ──────────────────────────────────────────
  "@keyframes latir": {
    "0%":   { transform: "scale(1)", opacity: 1 },
    "50%":  { transform: "scale(1.35)", opacity: 0.55 },
    "100%": { transform: "scale(1)", opacity: 1 },
  },
  "@keyframes entrar": {
    "0%":   { transform: "translateX(-10px)", opacity: 0, background: "#FEF3C7" },
    "60%":  { background: "#FEF9E7" },
    "100%": { transform: "translateX(0)", opacity: 1, background: "transparent" },
  },
  "@keyframes saltito": {
    "0%":   { transform: "scale(1)" },
    "35%":  { transform: "scale(1.18)", color: "#4338CA" },
    "100%": { transform: "scale(1)" },
  },
  // Se anima transform y NO el atributo `r`: las propiedades geométricas de SVG por
  // CSS son terreno resbaloso en Safari, mientras que transform anda en todos.
  "@keyframes ondaMapa": {
    "0%":   { transform: "scale(0.55)", opacity: 0.7, strokeWidth: 2.5 },
    "100%": { transform: "scale(2.6)", opacity: 0, strokeWidth: 0.5 },
  },
  "@keyframes caerPapelito": {
    "0%":   { transform: "translateY(-12vh) rotate(0deg)", opacity: 1 },
    "100%": { transform: "translateY(105vh) rotate(720deg)", opacity: 0.9 },
  },
  "@keyframes irrumpir": {
    "0%":   { transform: "scale(0.7) translateY(24px)", opacity: 0 },
    "55%":  { transform: "scale(1.06) translateY(0)", opacity: 1 },
    "100%": { transform: "scale(1) translateY(0)", opacity: 1 },
  },
  "@keyframes brilloVenta": {
    "0%, 100%": { boxShadow: "0 18px 60px rgba(22,163,74,0.45)" },
    "50%":      { boxShadow: "0 18px 90px rgba(22,163,74,0.85)" },
  },

  latiendo: { animation: "$latir 1.6s ease-in-out infinite" },
  entrando: { animation: "$entrar 1.1s ease-out" },
  saltando: { animation: "$saltito 0.6s ease-out", display: "inline-block" },
  // OJO: JSS renombra los @keyframes, así que el nombre SÓLO se puede referenciar con
  // $nombre desde dentro de esta hoja. Por eso la animación se aplica con estas clases
  // y lo que varía por elemento (duración, delay) va por style inline, que sí se puede.
  onda: { animation: "$ondaMapa 3s ease-out infinite" },
  ondaVenta: { animation: "$ondaMapa 1.5s ease-out infinite" },
  cayendo: { animation: "$caerPapelito 3s linear forwards" },

  // Celebración de venta: ocupa la pantalla a propósito. Es EL evento del tablero.
  velo: {
    position: "fixed", inset: 0, zIndex: 12000, pointerEvents: "none",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "radial-gradient(circle at center, rgba(22,163,74,0.16), rgba(15,23,42,0.55))",
  },
  tarjetaVenta: {
    pointerEvents: "auto", background: "#fff", borderRadius: 20, padding: "26px 34px",
    textAlign: "center", border: "2px solid #16A34A", maxWidth: 420,
    animation: "$irrumpir 0.55s cubic-bezier(.2,1.3,.4,1), $brilloVenta 2s ease-in-out infinite",
  },
  papelito: { position: "fixed", top: 0, width: 9, height: 14, borderRadius: 2, zIndex: 12001, pointerEvents: "none" },

  "@keyframes globoEntra": {
    "0%":   { transform: "translateX(120%) scale(0.9)", opacity: 0 },
    "70%":  { transform: "translateX(-6px) scale(1.02)", opacity: 1 },
    "100%": { transform: "translateX(0) scale(1)", opacity: 1 },
  },
  "@keyframes globoSale": {
    "0%":   { transform: "translateX(0)", opacity: 1, maxHeight: 160 },
    "100%": { transform: "translateX(120%)", opacity: 0, maxHeight: 0, marginBottom: 0 },
  },
  // Pila de globitos abajo a la derecha. No tapa el contenido y se va sola.
  globos: {
    position: "fixed", right: 18, bottom: 18, zIndex: 11500,
    display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8,
    maxWidth: 340, pointerEvents: "none",
  },
  globo: {
    pointerEvents: "auto", cursor: "pointer", background: "#fff", borderRadius: 14,
    border: "1px solid #E2E8F0", borderLeft: "4px solid #4338CA",
    boxShadow: "0 10px 30px rgba(15,23,42,0.16)", padding: "10px 13px", width: "100%",
    animation: "$globoEntra 0.42s cubic-bezier(.2,1.2,.3,1)",
  },
  globoVenta: { borderLeftColor: "#16A34A" },
  globoSaliendo: { animation: "$globoSale 0.45s ease-in forwards" },
});

/**
 * Cómo se ve cada tipo de evento en los globitos. El color es lo que permite leer la
 * salud de un vistazo sin detenerse a leer el texto.
 */
const TIPO = {
  // "●" y no "•": dentro de la pastilla el punto tipográfico chico casi no se veía.
  navegacion: { color: "#6366F1", icono: "●", nombre: "Navegación" },
  alumno:     { color: "#0D9488", icono: "🎓", nombre: "Alumno" },
  campana:    { color: "#EC4899", icono: "⚡", nombre: "Llegó de campaña" },
  consulta:   { color: "#0EA5E9", icono: "✉", nombre: "Consulta" },
  embudo:     { color: "#F59E0B", icono: "▸", nombre: "Embudo" },
  venta:      { color: "#16A34A", icono: "★", nombre: "Venta" },
};
/** Si la API agrega un tipo nuevo, la vista no se cae: cae en el genérico. */
const tipoDe = (k) => TIPO[k] || { color: "#94A3B8", icono: "◦", nombre: k || "Evento" };

/** Cuántos eventos se guardan en la cinta. Más que esto ya nadie scrollea. */
const CINTA_MAX = 150;

/** El stream viene del más nuevo al más viejo; la cinta se lee al revés, como un chat. */
const cronologico = (arr) => arr.slice().sort((a, b) => (a.orden || 0) - (b.orden || 0));

/**
 * Agrupa filas seguidas idénticas en una sola con contador, como hace un chat con los
 * mensajes del mismo autor. Sin esto, veinte "Entró al sitio · celular" de Perú ocupan
 * toda la cinta y tapan la consulta o la venta que sí importa.
 *
 * El detalle entra en la clave a propósito: dos consultas de WhatsApp comparten título
 * pero son personas distintas y no se pueden juntar.
 */
const agrupar = (lista) => {
  const out = [];
  lista.forEach((x) => {
    const clave = [x.tipo, x.titulo, x.detalle || "", x.country || ""].join("|");
    const ult = out[out.length - 1];
    // Las consultas nunca se agrupan: cada una se abre en la bandeja por su propio id.
    if (ult && ult.grupo === clave && !x.msg_id) {
      ult.veces += 1;
      ult.ts = x.ts;
      ult.clave = x.clave;
      ult.nuevo = x.nuevo;
      return;
    }
    out.push({ ...x, grupo: clave, veces: 1 });
  });

  return out;
};

/** Clave estable de una venta, para saber si ya la vimos. */
const claveVenta = (v) => `${v.created_at}|${v.iso}|${v.price}|${v.plan}`;

/**
 * Campanita de venta generada con WebAudio: sin archivo ni CDN, dos notas cortas.
 * Puede fallar si el navegador todavía no tuvo un gesto del usuario — no importa,
 * la celebración visual es la que manda.
 */
function sonarVenta() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [[880, 0], [1318.5, 0.13]].forEach(([hz, t]) => {
      const osc = ctx.createOscillator();
      const gan = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = hz;
      gan.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      gan.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + t + 0.02);
      gan.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.42);
      osc.connect(gan); gan.connect(ctx.destination);
      osc.start(ctx.currentTime + t); osc.stop(ctx.currentTime + t + 0.45);
    });
    setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1200);
  } catch (e) { /* sin sonido y listo */ }
}

const COLORES_PAPELITO = ["#16A34A", "#4338CA", "#F59E0B", "#EF4444", "#06B6D4", "#EC4899"];

/** Confeti en DOM puro: 70 papelitos con caída y giro aleatorios. */
function Confeti({ classes }) {
  const piezas = React.useMemo(
    () => Array.from({ length: 70 }, () => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.7,
      dur: 2.4 + Math.random() * 1.8,
      color: COLORES_PAPELITO[Math.floor(Math.random() * COLORES_PAPELITO.length)],
      escala: 0.7 + Math.random() * 0.9,
    })),
    []
  );
  return (
    <>
      {piezas.map((p, i) => (
        <div key={i} className={`${classes.papelito} ${classes.cayendo}`}
          style={{
            left: `${p.left}%`, background: p.color,
            animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s`,
          }} />
      ))}
    </>
  );
}

export default function Realtime() {
  const classes = useStyles();
  const [mins, setMins] = useState(60);
  const [audiencia, setAudiencia] = useState(() => {
    try { return localStorage.getItem("tcdg_rt_audiencia") || "todos"; } catch (e) { return "todos"; }
  });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pausado, setPausado] = useState(false);
  const timer = useRef(null);

  // ── Detección de novedades ──────────────────────────────
  // null = todavía no hay línea de base. Importa: en la PRIMERA carga todo es
  // "nuevo" y no hay que festejar cinco ventas viejas al abrir el tablero.
  const ventasVistas = useRef(null);
  const consultasVistas = useRef(null);
  const [festejo, setFestejo] = useState(null);      // venta a celebrar
  const [nuevas, setNuevas] = useState({});          // ids de consultas recién llegadas
  const [globos, setGlobos] = useState([]);          // toasts de consultas que van cayendo
  const [burbujas, setBurbujas] = useState([]);      // globitos animados sobre el mapa
  const streamVisto = useRef(null);
  const [pop, setPop] = useState({});                // contadores que acaban de subir

  // ── Cinta de actividad ──────────────────────────────────
  // Se acumula en el front (la API sólo devuelve la ventana actual), así que lo que ya
  // pasó por la cinta se queda ahí aunque salga del stream.
  const [cinta, setCinta] = useState([]);
  const [cintaAbierta, setCintaAbierta] = useState(() => {
    try { return localStorage.getItem("tcdg_rt_cinta") !== "0"; } catch (e) { return true; }
  });
  // Mientras el sitio no manda su propia telemetría, la navegación es casi toda "entró
  // al sitio" sin ruta ni campaña: se puede sacar de la cinta y quedarse con lo fuerte.
  const [soloFuerte, setSoloFuerte] = useState(() => {
    try { return localStorage.getItem("tcdg_rt_cinta_fuerte") === "1"; } catch (e) { return false; }
  });
  const listaRef = useRef(null);
  // Si el usuario scrolleó para arriba a leer algo, la cinta NO se lo arrebata.
  const pegadoAbajo = useRef(true);
  const [hayNuevos, setHayNuevos] = useState(false);
  const timersCinta = useRef([]);
  const previos = useRef({});
  const [sonido, setSonido] = useState(() => {
    try { return localStorage.getItem("tcdg_rt_sonido") !== "0"; } catch (e) { return true; }
  });
  const sonidoRef = useRef(sonido);
  useEffect(() => {
    sonidoRef.current = sonido;
    try { localStorage.setItem("tcdg_rt_sonido", sonido ? "1" : "0"); } catch (e) {}
  }, [sonido]);

  const fetchData = useCallback(() => {
    fetch(`${API_URI}/admin/activity/live?minutes=${mins}&audiencia=${audiencia}`)
      .then((r) => r.json())
      .then((res) => {
        if (res && res.result) { setData(res); setError(null); }
        else setError("La respuesta del servidor no vino bien");
      })
      .catch(() => setError("No se pudo cargar (error de red)"));
  }, [mins, audiencia]);

  useEffect(() => {
    try { localStorage.setItem("tcdg_rt_audiencia", audiencia); } catch (e) {}
  }, [audiencia]);

  // Al cambiar de ventana (o de audiencia) los conjuntos cambian entero: se rehace la
  // línea de base para no festejar ventas que simplemente entraron en el nuevo rango, y
  // la cinta se resiembra en vez de mostrar la mezcla de los dos filtros.
  useEffect(() => {
    ventasVistas.current = null;
    consultasVistas.current = null;
    streamVisto.current = null;
  }, [mins, audiencia]);

  /**
   * Compara cada respuesta con la anterior y dispara las animaciones. Todo lo que
   * marca como "nuevo" se limpia solo a los pocos segundos.
   */
  useEffect(() => {
    if (!data) return;

    // ── Ventas ──
    const claves = (data.sales || []).map(claveVenta);
    if (ventasVistas.current === null) {
      ventasVistas.current = new Set(claves);
    } else {
      const frescas = (data.sales || []).filter((v) => !ventasVistas.current.has(claveVenta(v)));
      claves.forEach((k) => ventasVistas.current.add(k));
      if (frescas.length) {
        setFestejo({ venta: frescas[0], extra: frescas.length - 1 });
        if (sonidoRef.current) sonarVenta();
      }
    }

    // ── Consultas nuevas ──
    const ids = ((data.inquiries && data.inquiries.recientes) || []).map((r) => r.id);
    if (consultasVistas.current === null) {
      consultasVistas.current = new Set(ids);
    } else {
      const frescas = ids.filter((id) => !consultasVistas.current.has(id));
      ids.forEach((id) => consultasVistas.current.add(id));
      if (frescas.length) {
        // Globitos: sólo de las 3 más nuevas. Si entran 20 comentarios de YouTube de
        // golpe no queremos una avalancha tapando la pantalla.
        const porId = {};
        (data.inquiries.recientes || []).forEach((r) => { porId[r.id] = r; });
        const paraGlobo = frescas.slice(0, 3).map((id) => porId[id]).filter(Boolean);
        if (paraGlobo.length) {
          setGlobos((prev) => [...paraGlobo.map((r) => ({ ...r, saliendo: false })), ...prev].slice(0, 4));
          paraGlobo.forEach((r) => {
            // Primero se marca "saliendo" para que se anime, y recién después se quita.
            setTimeout(() => setGlobos((prev) => prev.map((g) => (g.id === r.id ? { ...g, saliendo: true } : g))), 7200);
            setTimeout(() => setGlobos((prev) => prev.filter((g) => g.id !== r.id)), 7700);
          });
        }
        setNuevas((prev) => {
          const n = { ...prev };
          frescas.forEach((id) => { n[id] = true; });
          return n;
        });
        setTimeout(() => setNuevas((prev) => {
          const n = { ...prev };
          frescas.forEach((id) => delete n[id]);
          return n;
        }), 6000);
      }
    }

    // ── Globitos del mapa: sólo lo que no vimos antes ──
    const st = data.stream || [];
    if (streamVisto.current === null) {
      // Primera carga: se toma como línea de base para no vomitar 45 globitos al abrir.
      streamVisto.current = new Set(st.map((x) => x.id));
      // La cinta sí arranca con historia: abrir el tablero y ver una lista vacía haría
      // pensar que está roto. Se siembra en orden cronológico (lo viejo arriba).
      setCinta(cronologico(st).slice(-CINTA_MAX).map((x) => ({ ...x, clave: x.id, nuevo: false })));
    } else {
      const frescos = st.filter((x) => !streamVisto.current.has(x.id));
      st.forEach((x) => streamVisto.current.add(x.id));
      if (frescos.length) {
        // La cinta los va soltando de a uno para que se lea como un chat que corre, no
        // como un volcado de 12 filas cada 20 segundos. El paso se ajusta para que la
        // tanda entera entre ANTES del próximo refresco: si no, se apilarían.
        const paso = Math.min(700, Math.floor(16000 / frescos.length));
        timersCinta.current = [];   // la tanda anterior ya cayó entera
        cronologico(frescos).forEach((x, i) => {
          timersCinta.current.push(setTimeout(() => {
            setCinta((prev) => [...prev, { ...x, clave: x.id, nuevo: true }].slice(-CINTA_MAX));
          }, i * paso));
        });
        // Se muestran de a poco (escalonados) para que se vea el movimiento en vez de
        // aparecer todos juntos. Se priorizan ventas y embudo: son lo que importa.
        const orden = { venta: 0, embudo: 1, campana: 2, consulta: 3, navegacion: 4 };
        const elegidos = frescos
          .sort((a, b) => (orden[a.tipo] ?? 9) - (orden[b.tipo] ?? 9))
          .slice(0, 12);
        // Coordenadas por país, para los eventos que traen país pero no lat/lon
        // (consultas de email, ventas): se reusa el promedio que ya calcula la API.
        const coordPais = {};
        (data.countries || []).forEach((c) => { if (c.lat !== null && c.lon !== null) coordPais[c.iso] = c; });

        const posicionar = (x) => {
          let lat = x.lat, lon = x.lon;
          if ((lat === null || lat === undefined) && x.country && coordPais[x.country]) {
            lat = coordPais[x.country].lat; lon = coordPais[x.country].lon;
          }
          if (lat === null || lat === undefined || lon === null || lon === undefined) return null;
          const { x: px, y: py } = proyectar(Number(lat), Number(lon));
          // Pizca de dispersión para que dos globitos del mismo país no se tapen.
          return {
            xp: Math.max(4, Math.min(96, (px / MAPA.w) * 100 + (Math.random() - 0.5) * 6)),
            yp: Math.max(10, Math.min(94, (py / MAPA.h) * 100 + (Math.random() - 0.5) * 8)),
          };
        };

        elegidos.forEach((x, i) => {
          setTimeout(() => {
            const clave = x.id + ":" + Math.random().toString(36).slice(2, 7);
            setBurbujas((prev) => [...prev, { ...x, clave, pos: posicionar(x) }].slice(-14));
            setTimeout(() => setBurbujas((prev) => prev.filter((b) => b.clave !== clave)), 6200);
          }, i * 420);
        });
      }
    }

    // ── Contadores que subieron ──
    const t = data.totals || {};
    const q = data.inquiries || {};
    const ahora = { visitantes: t.visitantes, consultas: q.total, intentos: t.intentos_pago, ventas: t.ventas };
    const subieron = {};
    Object.keys(ahora).forEach((k) => {
      if (previos.current[k] !== undefined && ahora[k] > previos.current[k]) subieron[k] = true;
    });
    previos.current = ahora;
    if (Object.keys(subieron).length) {
      setPop(subieron);
      setTimeout(() => setPop({}), 900);
    }
  }, [data]);

  // La cinta sigue al último evento sola, salvo que el usuario se haya ido para arriba
  // a leer: en ese caso queda quieta y aparece el botón de "nuevos".
  useEffect(() => {
    const el = listaRef.current;
    if (!el || !cinta.length) return;
    if (pegadoAbajo.current) {
      el.scrollTop = el.scrollHeight;
      setHayNuevos(false);
    } else {
      setHayNuevos(true);
    }
  }, [cinta, cintaAbierta]);

  const alScrollear = () => {
    const el = listaRef.current;
    if (!el) return;
    const abajo = el.scrollHeight - el.scrollTop - el.clientHeight < 28;
    pegadoAbajo.current = abajo;
    if (abajo) setHayNuevos(false);
  };

  const irAbajo = () => {
    const el = listaRef.current;
    if (!el) return;
    pegadoAbajo.current = true;
    el.scrollTop = el.scrollHeight;
    setHayNuevos(false);
  };

  useEffect(() => {
    try { localStorage.setItem("tcdg_rt_cinta", cintaAbierta ? "1" : "0"); } catch (e) {}
  }, [cintaAbierta]);
  useEffect(() => {
    try { localStorage.setItem("tcdg_rt_cinta_fuerte", soloFuerte ? "1" : "0"); } catch (e) {}
  }, [soloFuerte]);

  // Lo que se ve en la cinta: filtrado y con las filas repetidas juntas.
  const cintaVisible = useMemo(
    () => agrupar(soloFuerte ? cinta.filter((x) => x.tipo !== "navegacion") : cinta),
    [cinta, soloFuerte]
  );

  // Al cambiar de ventana o de audiencia (o al salir) hay que matar la cola de entradas
  // escalonadas: si no, siguen cayendo eventos del filtro anterior sobre la cinta nueva.
  useEffect(() => {
    return () => {
      timersCinta.current.forEach(clearTimeout);
      timersCinta.current = [];
    };
  }, [mins, audiencia]);

  // El festejo se va solo: es información, no un modal que haya que cerrar.
  useEffect(() => {
    if (!festejo) return;
    const t = setTimeout(() => setFestejo(null), 11000);
    return () => clearTimeout(t);
  }, [festejo]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (pausado) return;
    // 20s: suficiente para sentirlo vivo sin castigar la base.
    timer.current = setInterval(fetchData, 20000);
    return () => clearInterval(timer.current);
  }, [fetchData, pausado]);

  const t = (data && data.totals) || {};
  const salud = (data && data.health) || {};
  const paises = (data && data.countries) || [];
  const maxVis = paises.reduce((m, p) => Math.max(m, p.visitantes), 0) || 1;

  const delta = (act, prev) => {
    if (!prev) return null;
    const d = Math.round(((act - prev) / prev) * 100);
    if (!isFinite(d) || d === 0) return null;
    return d;
  };
  const dVis = delta(t.visitantes, t.visitantes_prev);

  return (
    <div className={classes.root}>
      {/* ── Celebración de venta ── */}
      {festejo && (() => {
        const v = festejo.venta;
        return (
          <>
            <Confeti classes={classes} />
            <div className={classes.velo} onClick={() => setFestejo(null)}>
              <div className={classes.tarjetaVenta}>
                <div style={{ fontSize: 46, lineHeight: 1 }}>🎸</div>
                <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.2, color: "#16A34A", marginTop: 6 }}>
                  ¡VENTA!
                </div>
                <div style={{ fontSize: 34, fontWeight: 800, margin: "6px 0 2px" }}>
                  {v.simbolo} {v.price} <span style={{ fontSize: 16, color: "#64748B" }}>{v.coin}</span>
                </div>
                <div style={{ fontSize: 15, color: "#334155" }}>
                  {ISO_FLAG(v.iso)} {v.pais} · {v.plan || "—"}
                </div>
                {v.price_usd ? (
                  <div style={{ fontSize: 12.5, color: "#64748B", marginTop: 4 }}>US$ {nf(v.price_usd)} · {v.canal || "—"}</div>
                ) : null}
                {festejo.extra > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 700, color: "#4338CA" }}>
                    +{festejo.extra} venta{festejo.extra === 1 ? "" : "s"} más en esta tanda
                  </div>
                )}
                <div style={{ marginTop: 10, fontSize: 11, color: "#94A3B8" }}>se cierra solo · clic para cerrar</div>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── Globitos de consultas que van cayendo ── */}
      {!!globos.length && (
        <div className={classes.globos}>
          {globos.map((g) => {
            const esVenta = g.venta !== null && g.venta !== undefined && g.venta >= 0.5;
            return (
              <div key={g.id}
                className={`${classes.globo} ${esVenta ? classes.globoVenta : ""} ${g.saliendo ? classes.globoSaliendo : ""}`}
                onClick={() => { window.location.hash = `#/inbox/${g.id}`; }}
                title="Abrir en la bandeja">
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, fontSize: 11.5 }}>
                  <span style={{ fontWeight: 800, color: esVenta ? "#16A34A" : "#4338CA" }}>
                    {g.medio}{g.buzon ? ` ${g.buzon}` : ""}
                  </span>
                  <span style={{ color: "#94A3B8" }}>{g.hora}</span>
                  {esVenta && (
                    <span style={{ marginLeft: "auto", background: "#DCFCE7", color: "#166534", borderRadius: 999, padding: "0 6px", fontSize: 10, fontWeight: 800 }}>
                      {Math.round(g.venta * 100)}% venta
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {g.de || "—"}
                </div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 1, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {g.texto || <i>(sin texto)</i>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={classes.bar}>
        <div className={classes.title}>Tiempo real</div>
        {VENTANAS.map((w) => (
          <button key={w.v} className={`${classes.chip} ${mins === w.v ? classes.chipOn : ""}`}
            onClick={() => setMins(w.v)}>{w.l}</button>
        ))}
        <span className={classes.separador} />
        {AUDIENCIAS.map((a) => (
          <button key={a.v} className={`${classes.chip} ${audiencia === a.v ? classes.chipAud : ""}`}
            onClick={() => setAudiencia(a.v)} title={a.t}>{a.l}</button>
        ))}
        <button className={`${classes.chip} ${pausado ? classes.chipOn : ""}`}
          onClick={() => setPausado((p) => !p)}
          title="Pausar el refresco automático">{pausado ? "Pausado" : "En vivo"}</button>
        <button className={classes.chip} onClick={() => setSonido((s) => !s)}
          title={sonido ? "Silenciar la campanita de venta" : "Activar la campanita de venta"}>
          {sonido ? "🔔" : "🔇"}
        </button>
        <span className={classes.pulse}>
          <span className={`${classes.dot} ${salud.instrumentacion_ok ? "" : classes.dotOff} ${!pausado && salud.instrumentacion_ok ? classes.latiendo : ""}`} />
          {data ? `actualizado ${new Date(data.generated).toLocaleTimeString("es-AR")}` : "cargando…"}
        </span>
      </div>

      {error && <div className={classes.banner}>{error}</div>}

      {/* Un mapa vacío puede ser "no hay tráfico" o "se rompió la instrumentación".
          Son cosas muy distintas y el tablero tiene que decir cuál. */}
      {data && !salud.instrumentacion_ok && (
        <div className={classes.banner}>
          <b>Sin eventos desde hace {salud.minutos_sin_eventos === null ? "siempre" : `${salud.minutos_sin_eventos} min`}.</b>{" "}
          Con tráfico normal entra actividad cada pocos minutos, así que esto probablemente sea la
          instrumentación cortada y no la falta de visitas. Último evento: {salud.ultimo_evento || "nunca"}.
        </div>
      )}

      <div className={classes.cards}>
        <div className={classes.card}>
          <div className={classes.cardLbl}>
            Visitantes{audiencia === "no_alumnos" ? " · sin alumnos" : audiencia === "alumnos" ? " · alumnos" : ""}
          </div>
          <div className={classes.cardVal}><span className={pop.visitantes ? classes.saltando : ""}>{nf(t.visitantes)}</span></div>
          <div className={classes.cardSub}>
            por {t.medida_visitante === "sesiones" ? "sesión" : t.medida_visitante === "mixta" ? "sesión o IP" : "IP"}
            {dVis !== null && <> · <span className={dVis > 0 ? classes.up : classes.down}>{dVis > 0 ? "▲" : "▼"} {Math.abs(dVis)}%</span></>}
          </div>
        </div>
        {/* Alumnos vs. gente sin cuenta: el desglose sale SIEMPRE del total, no de la
            vista filtrada, así el porcentaje sigue queriendo decir lo mismo con el filtro
            puesto. Y si el enganche todavía no vio ningún alumno, dice "sin dato": un 0
            se leería como "no hay alumnos navegando", que es otra cosa. */}
        <div className={classes.card}>
          <div className={classes.cardLbl}>Sin cuenta / alumnos</div>
          {t.alumnos === null || t.alumnos === undefined ? (
            <>
              <div className={classes.cardVal} style={{ fontSize: 18, color: "#94A3B8" }}>sin dato</div>
              <div className={classes.cardSub}>todavía no se identificó ningún alumno</div>
            </>
          ) : (
            <>
              <div className={classes.cardVal}>
                {nf(t.no_alumnos)} <span style={{ fontSize: 15, color: "#94A3B8" }}>/ {nf(t.alumnos)}</span>
              </div>
              <div className={classes.cardSub}>
                {t.visitantes_total > 0
                  ? `${Math.round((t.no_alumnos / t.visitantes_total) * 100)}% sin cuenta de ${nf(t.visitantes_total)}`
                  : "sin visitantes en la ventana"}
              </div>
            </>
          )}
        </div>
        <div className={classes.card}>
          <div className={classes.cardLbl}>Países</div>
          <div className={classes.cardVal}>{nf(t.paises)}</div>
          <div className={classes.cardSub}>con actividad</div>
        </div>
        <div className={classes.card}>
          <div className={classes.cardLbl}>Consultas nuevas</div>
          <div className={classes.cardVal}><span className={pop.consultas ? classes.saltando : ""}>{nf((data && data.inquiries && data.inquiries.total) || 0)}</span></div>
          <div className={classes.cardSub}>conversaciones en la ventana</div>
        </div>
        <div className={classes.card}>
          <div className={classes.cardLbl}>Intentos de pago</div>
          <div className={classes.cardVal}><span className={pop.intentos ? classes.saltando : ""}>{nf(t.intentos_pago)}</span></div>
          <div className={classes.cardSub}>llegaron al pago</div>
        </div>
        <div className={classes.card}>
          <div className={classes.cardLbl}>Ventas</div>
          <div className={classes.cardVal} style={{ color: t.ventas > 0 ? "#16A34A" : undefined }}><span className={pop.ventas ? classes.saltando : ""}>{nf(t.ventas)}</span></div>
          <div className={classes.cardSub}>US$ {nf(t.ingreso_usd)}</div>
        </div>
        <div className={classes.card}>
          <div className={classes.cardLbl}>Conversión</div>
          {/* Con pocos visitantes el porcentaje es ruido puro: mejor no mostrarlo. */}
          <div className={classes.cardVal}>{t.visitantes >= 30 && t.conversion !== null ? `${t.conversion}%` : "—"}</div>
          <div className={classes.cardSub}>{t.visitantes >= 30 ? "ventas / visitantes" : "hace falta más volumen"}</div>
        </div>
        <div className={classes.card}>
          <div className={classes.cardLbl}>Última venta</div>
          <div className={classes.cardVal} style={{ fontSize: 18 }}>
            {salud.horas_sin_ventas === null ? "—" : salud.horas_sin_ventas < 1 ? "hace menos de 1 h" : `hace ${salud.horas_sin_ventas} h`}
          </div>
          <div className={classes.cardSub}>{salud.ultima_venta || "sin registro"}</div>
        </div>
      </div>

      {/* ── Mapa apaisado: la pieza central. Los globitos van mostrando lo que pasa. ── */}
      <div className={classes.mapaPanel}>
        <div className={classes.panelTitle}>
          Comportamiento en vivo
          <span className={classes.hint}>
            · tamaño del punto = visitantes · el globito aparece y se va · la cinta guarda el orden
          </span>
        </div>

        <div className={classes.lienzo}>
          <svg viewBox={`0 0 ${MAPA.w} ${MAPA.h}`} style={{ width: "100%", display: "block", background: "#F8FAFC", borderRadius: 10 }}>
            {[-120, -90, -60, -30, 0, 30].map((lon) => {
              const { x } = proyectar(0, lon);
              return <line key={`x${lon}`} x1={x} y1={0} x2={x} y2={MAPA.h} stroke="#E9EEF5" strokeWidth="1" />;
            })}
            {[60, 30, 0, -30].map((lat) => {
              const { y } = proyectar(lat, 0);
              return (
                <g key={`y${lat}`}>
                  <line x1={0} y1={y} x2={MAPA.w} y2={y} stroke="#E9EEF5" strokeWidth="1" />
                  <text x={5} y={y - 4} fontSize="9" fill="#B6C2D2">{lat === 0 ? "ecuador" : `${lat}°`}</text>
                </g>
              );
            })}

            {/* La tierra va DESPUÉS de la grilla: el relleno es opaco, así los paralelos
                se ven sólo sobre el agua y no cruzan los continentes. */}
            <path d={TIERRA_PATH} fill="#DCE5F0" stroke="#B5C3D6" strokeWidth="0.7" strokeLinejoin="round" />

            {paises.map((p) => {
              if (p.lat === null || p.lon === null) return null;
              const { x, y } = proyectar(p.lat, p.lon);
              const r = 5 + Math.sqrt(p.visitantes / maxVis) * 22;
              const conVenta = p.ventas > 0;
              return (
                <g key={p.iso}>
                  <circle cx={x} cy={y} r={r} fill="none" stroke={conVenta ? "#16A34A" : "#4338CA"}
                    className={conVenta ? classes.ondaVenta : classes.onda}
                    style={{ transformOrigin: `${x}px ${y}px` }} />
                  <circle cx={x} cy={y} r={r} fill={conVenta ? "#22C55E" : "#4338CA"} fillOpacity="0.26"
                    stroke={conVenta ? "#16A34A" : "#4338CA"} strokeWidth="1.5" />
                  <title>{`${p.nombre}: ${p.visitantes} visitantes · ${p.intentos} al pago · ${p.ventas} ventas`}</title>
                  {p.visitantes / maxVis > 0.12 && (
                    <text x={x} y={y + 3.5} fontSize="10" fontWeight="700" textAnchor="middle" fill="#1E293B">{p.iso}</text>
                  )}
                </g>
              );
            })}
            {!paises.length && (
              <text x={MAPA.w / 2} y={MAPA.h / 2} fontSize="13" textAnchor="middle" fill="#94A3B8">
                sin actividad en esta ventana
              </text>
            )}
          </svg>

          {/* Globitos anclados al país. Los que no tienen coordenadas (email, comentarios)
              van al carril de abajo a la izquierda en vez de inventarles una posición. */}
          <div className={classes.capaGlobos}>
            {burbujas.filter((b) => b.pos).map((b) => (
              <div key={b.clave} className={classes.burbuja}
                style={{ left: `${b.pos.xp}%`, top: `${b.pos.yp}%`, borderColor: tipoDe(b.tipo).color }}>
                <span style={{ color: tipoDe(b.tipo).color, marginRight: 4 }}>{tipoDe(b.tipo).icono}</span>
                {b.country ? `${ISO_FLAG(b.country)} ` : ""}{b.titulo}
                {b.detalle && <span style={{ fontWeight: 400, color: "#64748B" }}> · {String(b.detalle).slice(0, 26)}</span>}
              </div>
            ))}
            <div className={classes.carril}>
              {burbujas.filter((b) => !b.pos).map((b) => (
                <div key={b.clave} className={classes.chipCarril} style={{ borderColor: tipoDe(b.tipo).color }}>
                  <span style={{ color: tipoDe(b.tipo).color, marginRight: 4 }}>{tipoDe(b.tipo).icono}</span>
                  {b.titulo}
                  {b.detalle && <span style={{ fontWeight: 400, color: "#64748B" }}> · {String(b.detalle).slice(0, 26)}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* ── Cinta: todo lo que pasa, en orden, corriendo como un chat ── */}
          {!cintaAbierta ? (
            <button className={classes.cintaPlegada} onClick={() => setCintaAbierta(true)}
              title="Mostrar la cinta de actividad">‹ actividad</button>
          ) : (
            <div className={classes.cinta}>
              <div className={classes.cintaCab}>
                <span className={`${classes.dot} ${pausado ? "" : classes.latiendo}`}
                  style={{ background: pausado ? "#EF4444" : "#22C55E" }} />
                En vivo
                <button className={classes.cintaBoton} style={{ marginLeft: "auto" }}
                  onClick={() => setSoloFuerte((v) => !v)}
                  title={soloFuerte
                    ? "Mostrando sólo campañas, consultas, embudo y ventas"
                    : "Mostrando todo, incluidas las entradas al sitio"}>
                  {soloFuerte ? "fuertes" : "todo"}
                </button>
                <button className={classes.cintaBoton} onClick={() => setCintaAbierta(false)} title="Plegar">›</button>
              </div>

              <div className={classes.cintaLista} ref={listaRef} onScroll={alScrollear}>
                {!cintaVisible.length ? (
                  <div className={classes.vacio} style={{ fontSize: 12 }}>
                    {soloFuerte
                      ? "Sin campañas, consultas ni movimientos del embudo en esta ventana."
                      : "Todavía no entró ningún evento en esta ventana."}
                  </div>
                ) : cintaVisible.map((x) => {
                  const tp = tipoDe(x.tipo);
                  const esVenta = x.tipo === "venta";
                  const alInbox = x.msg_id ? () => { window.location.hash = `#/inbox/${x.msg_id}`; } : undefined;
                  return (
                    <div key={x.clave}
                      className={[
                        classes.cintaFila,
                        x.nuevo ? classes.cintaFilaNueva : "",
                        esVenta ? classes.cintaFilaVenta : "",
                        alInbox ? classes.cintaClickeable : "",
                      ].join(" ")}
                      style={{ borderLeftColor: tp.color }}
                      onClick={alInbox}
                      title={alInbox ? "Abrir en la bandeja" : tp.nombre}>
                      <span className={classes.cintaIcono}
                        style={{ color: tp.color, background: tp.color + "1F" }}
                        title={tp.nombre}>{tp.icono}</span>
                      <span className={classes.cintaHora}>{x.ts ? String(x.ts).slice(0, 5) : "--:--"}</span>
                      <span className={classes.cintaTexto}>
                        <span className={classes.cintaTitulo} style={esVenta ? { color: "#15803D" } : null}>
                          {x.country ? <span style={{ marginRight: 4 }}>{ISO_FLAG(x.country)}</span> : null}{x.titulo}
                          {x.veces > 1 && <span className={classes.cintaVeces}>×{x.veces}</span>}
                        </span>
                        {x.detalle && <span className={classes.cintaDetalle}>{x.detalle}</span>}
                      </span>
                    </div>
                  );
                })}
              </div>

              {hayNuevos && (
                <button className={classes.cintaAbajo} onClick={irAbajo}>↓ eventos nuevos</button>
              )}
            </div>
          )}
        </div>

        <div className={classes.leyenda}>
          {Object.keys(TIPO).map((k) => (
            <span key={k} className={classes.puntoLeyenda}>
              {/* Mismo tamaño que en la cinta, para que la leyenda se lea como su índice. */}
              <span style={{ color: TIPO[k].color, fontWeight: 800, fontSize: 14 }}>{TIPO[k].icono}</span> {TIPO[k].nombre}
            </span>
          ))}
          <span style={{ marginLeft: "auto" }}>
            El contorno es sólo referencia: los puntos van por las coordenadas del visitante, no por país
            pintado. Sin coordenadas → carril de la izquierda. La cinta se pliega con › para ver el mapa entero.
          </span>
        </div>
      </div>

      <div className={classes.grid3}>
        <div className={classes.panel}>
          
          <div className={classes.panelTitle}>Por país</div>
          {!paises.length ? <div className={classes.vacio}>Sin actividad todavía.</div> : (
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              <table className={classes.tabla}>
                <thead><tr>
                  <th className={classes.th}>País</th>
                  <th className={`${classes.th} ${classes.num}`}>Visit.</th>
                  <th className={`${classes.th} ${classes.num}`}>Pago</th>
                  <th className={`${classes.th} ${classes.num}`}>Ventas</th>
                </tr></thead>
                <tbody>
                  {paises.map((p) => (
                    <tr key={p.iso}>
                      <td className={classes.td} title={p.nombre}>
                        {ISO_FLAG(p.iso)} {p.nombre}
                        <div className={classes.barra} style={{ width: `${Math.max(4, (p.visitantes / maxVis) * 100)}%`, marginTop: 3, opacity: 0.55 }} />
                      </td>
                      <td className={`${classes.td} ${classes.num}`}>{nf(p.visitantes)}</td>
                      <td className={`${classes.td} ${classes.num}`}>{nf(p.intentos)}</td>
                      <td className={`${classes.td} ${classes.num}`} style={{ color: p.ventas ? "#16A34A" : undefined, fontWeight: p.ventas ? 700 : 400 }}>{nf(p.ventas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        
        </div>
        {/* ── Embudo ── */}
        <div className={classes.panel}>
          <div className={classes.panelTitle}>Embudo <span className={classes.hint}>· personas distintas</span></div>
          {(() => {
            const f = (data && data.funnel) || [];
            const top = Math.max(1, ...f.filter((e) => !e.sin_dato).map((e) => e.valor));
            return f.map((e, i) => {
              // Un paso sin dato no participa de los porcentajes: comparar contra un
              // número incompleto propaga la mentira al resto del embudo.
              const prevOk = i > 0 && !f[i - 1].sin_dato ? f[i - 1].valor : null;
              const pct = !e.sin_dato && prevOk ? Math.round((e.valor / prevOk) * 100) : null;
              return (
                <div key={e.etapa} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", fontSize: 12.5, marginBottom: 3 }}>
                    <span style={{ fontWeight: 600 }}>{e.etapa}</span>
                    <span style={{ marginLeft: "auto", fontVariantNumeric: "tabular-nums" }}>
                      {e.sin_dato
                        ? <span style={{ color: "#B45309", fontWeight: 700 }}>sin dato</span>
                        : <>{nf(e.valor)}{pct !== null && <span className={classes.hint}> · {pct}% del paso anterior</span>}</>}
                    </span>
                  </div>
                  <div style={{ height: 10, background: "#F1F5F9", borderRadius: 999 }}>
                    {e.sin_dato
                      ? <div style={{ height: "100%", borderRadius: 999, background: "repeating-linear-gradient(45deg,#FDE68A 0 6px,#FEF3C7 6px 12px)" }} />
                      : <div style={{ width: `${Math.max(2, (e.valor / top) * 100)}%`, height: "100%", background: "#4338CA", borderRadius: 999, opacity: 1 - i * 0.14 }} />}
                  </div>
                  <div className={classes.hint} style={{ marginTop: 2 }}>{e.motivo || e.fuente}</div>
                </div>
              );
            });
          })()}
          <div className={classes.hint} style={{ marginTop: 4, lineHeight: 1.5 }}>
            Las dos primeras etapas salen de la actividad del sitio y las dos últimas del checkout,
            así que no son un mismo conjunto que se va filtrando: leelas como cuatro medidas, no
            como una resta exacta.
          </div>
        </div>

        {/* ── Campañas ── */}
        <div className={classes.panel}>
          <div className={classes.panelTitle}>Campañas</div>
          {(() => {
            const c = (data && data.campaigns) || {};
            const traf = c.trafico || [];
            const ven = c.ventas || [];
            return (
              <>
                {c.sin_datos_de_trafico && (
                  <div className={classes.hint} style={{ marginBottom: 8, lineHeight: 1.5 }}>
                    Sin atribución de tráfico en esta ventana. Ojo: no significa que no haya campañas
                    activas — el sitio recién empieza a mandar utm cuando se deploya la instrumentación.
                  </div>
                )}
                {!!traf.length && (
                  <table className={classes.tabla} style={{ marginBottom: 12 }}>
                    <thead><tr>
                      <th className={classes.th}>Tráfico</th>
                      <th className={`${classes.th} ${classes.num}`}>Visit.</th>
                    </tr></thead>
                    <tbody>
                      {traf.slice(0, 8).map((r, i) => (
                        <tr key={i}>
                          <td className={classes.td} title={`${r.campaign} · ${r.source} · ${r.content}`}>
                            {r.campaign}<div className={classes.hint}>{r.source}{r.content !== "-" && ` · ${r.content}`}</div>
                          </td>
                          <td className={`${classes.td} ${classes.num}`}>{nf(r.visitantes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className={classes.panelTitle} style={{ fontSize: 12 }}>
                  Atribución de los que llegaron al pago <span className={classes.hint}>· de tracking_info</span>
                </div>
                {!ven.length ? <div className={classes.vacio}>Nadie llegó al pago en esta ventana.</div> : (
                  <table className={classes.tabla}>
                    <thead><tr>
                      <th className={classes.th}>Campaña</th>
                      <th className={`${classes.th} ${classes.num}`}>Intentos</th>
                      <th className={`${classes.th} ${classes.num}`}>Ventas</th>
                    </tr></thead>
                    <tbody>
                      {ven.slice(0, 8).map((r, i) => (
                        <tr key={i}>
                          <td className={classes.td} title={`${r.campaign} · ${r.source}`}>
                            {r.campaign}<div className={classes.hint}>{r.source}</div>
                          </td>
                          <td className={`${classes.td} ${classes.num}`}>{nf(r.intentos)}</td>
                          <td className={`${classes.td} ${classes.num}`} style={{ color: r.ventas ? "#16A34A" : undefined, fontWeight: r.ventas ? 700 : 400 }}>{nf(r.ventas)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            );
          })()}
        </div>

        {/* ── Pulso + dispositivos ── */}
        <div className={classes.panel}>
          <div className={classes.panelTitle}>
            Pulso <span className={classes.hint}>{data && data.timeline ? `· tramos de ${data.timeline.paso_minutos} min` : ""}</span>
          </div>
          {(() => {
            const pts = (data && data.timeline && data.timeline.puntos) || [];
            if (!pts.length) return <div className={classes.vacio}>Sin datos.</div>;
            const W = 300, H = 70;
            const max = Math.max(1, ...pts.map((p) => p.visitantes));
            const paso = pts.length > 1 ? W / (pts.length - 1) : W;
            const linea = pts.map((p, i) => `${i * paso},${H - (p.visitantes / max) * H}`).join(" ");
            return (
              <svg viewBox={`0 0 ${W} ${H + 16}`} style={{ width: "100%" }}>
                <polyline points={linea} fill="none" stroke="#4338CA" strokeWidth="2" />
                <polyline points={`0,${H} ${linea} ${(pts.length - 1) * paso},${H}`} fill="#4338CA" fillOpacity="0.10" stroke="none" />
                {pts.map((p, i) => p.ventas > 0 && (
                  <circle key={i} cx={i * paso} cy={H - (p.visitantes / max) * H} r="3.5" fill="#16A34A">
                    <title>{`${p.ts}: ${p.ventas} venta(s)`}</title>
                  </circle>
                ))}
                <text x="0" y={H + 13} fontSize="8" fill="#94A3B8">{pts[0].ts}</text>
                <text x={W} y={H + 13} fontSize="8" fill="#94A3B8" textAnchor="end">{pts[pts.length - 1].ts}</text>
              </svg>
            );
          })()}
          <div className={classes.hint} style={{ marginBottom: 10 }}>Máximo de visitantes por tramo · los puntos verdes son ventas</div>

          <div className={classes.panelTitle} style={{ fontSize: 12 }}>Dispositivos</div>
          {(() => {
            const d = (data && data.devices) || [];
            const tot = d.reduce((s, x) => s + x.visitantes, 0) || 1;
            if (!d.length) return <div className={classes.vacio}>Sin datos.</div>;
            return d.map((x) => (
              <div key={x.device} style={{ marginBottom: 6 }}>
                <div style={{ display: "flex", fontSize: 12.5 }}>
                  <span>{x.device}</span>
                  <span style={{ marginLeft: "auto" }}>{Math.round((x.visitantes / tot) * 100)}% · {nf(x.visitantes)}</span>
                </div>
                <div style={{ height: 6, background: "#F1F5F9", borderRadius: 999 }}>
                  <div className={classes.barra} style={{ width: `${(x.visitantes / tot) * 100}%` }} />
                </div>
              </div>
            ));
          })()}
        </div>

        {/* ── Consultas por medio ── */}
        <div className={classes.panel}>
          {(() => {
            const q = (data && data.inquiries) || {};
            // Sólo lo que pasó EN EL PERÍODO: los medios sin nada nuevo no se listan.
            // (La API igual devuelve sin_responder, que es el número del badge del inbox,
            // por si algún día se quiere volver a mostrar.)
            const medios = (q.por_medio || []).filter((m) => m.nuevas > 0);
            const max = medios.reduce((m, x) => Math.max(m, x.nuevas), 0) || 1;
            return (
              <>
                <div className={classes.panelTitle}>
                  Consultas por medio
                  <span className={classes.hint}>· conversaciones, sin spam</span>
                </div>

                {!medios.length ? (
                  <div className={classes.vacio}>Ninguna consulta entrante en esta ventana.</div>
                ) : medios.map((m) => (
                  <div key={m.medio} style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", fontSize: 12.5 }}>
                      <span style={{ fontWeight: 600 }}>{m.medio}</span>
                      <span style={{ marginLeft: "auto", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
                        {nf(m.nuevas)}
                      </span>
                    </div>
                    <div style={{ height: 6, background: "#F1F5F9", borderRadius: 999 }}>
                      <div className={classes.barra} style={{ width: `${Math.max(3, (m.nuevas / max) * 100)}%` }} />
                    </div>
                  </div>
                ))}
                {/* El backlog es de TODO el inbox, no de la ventana: es el número que
                    de verdad dice si estamos al día. */}
                <div className={classes.hint} style={{ marginTop: 10, lineHeight: 1.5 }}>
                  Conversaciones con algo entrante en la ventana, sin spam. Un comentarista que deja
                  diez comentarios cuenta como una.
                </div>

                {!!(q.recientes || []).length && (
                  <>
                    <div className={classes.panelTitle} style={{ fontSize: 12, marginTop: 12 }}>Últimas consultas</div>
                    <div style={{ maxHeight: 210, overflowY: "auto" }}>
                      {q.recientes.map((r) => (
                        <div key={r.id} className={nuevas[r.id] ? classes.entrando : ""}
                          style={{ padding: "5px 0", borderBottom: "1px solid #F1F5F9", fontSize: 12 }}>
                          <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
                            <span style={{ color: "#94A3B8" }}>{r.hora}</span>
                            <span style={{ fontWeight: 700, color: "#4338CA" }}>{r.medio}{r.buzon ? ` ${r.buzon}` : ""}</span>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.de || "—"}</span>
                            {r.venta !== null && r.venta >= 0.5 && (
                              <span style={{ marginLeft: "auto", background: "#FEF3C7", color: "#92400E", borderRadius: 999, padding: "0 6px", fontSize: 10, fontWeight: 800 }}>
                                {Math.round(r.venta * 100)}% venta
                              </span>
                            )}
                            {r.status === "pending" && r.venta === null && (
                              <span style={{ marginLeft: "auto", color: "#B45309", fontSize: 10.5, fontWeight: 700 }}>sin responder</span>
                            )}
                          </div>
                          <div style={{ color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.texto}>
                            {r.texto || <i>(sin texto)</i>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </div>

        {/* ── Últimas ventas ── */}
        <div className={classes.panel}>
          <div className={classes.panelTitle}>Últimas ventas</div>
          {(() => {
            const v = (data && data.sales) || [];
            if (!v.length) return <div className={classes.vacio}>Ninguna venta en esta ventana.</div>;
            return (
              <table className={classes.tabla}>
                <thead><tr>
                  <th className={classes.th}>Hora</th>
                  <th className={classes.th}>País</th>
                  <th className={classes.th}>Plan</th>
                  <th className={`${classes.th} ${classes.num}`}>Monto</th>
                </tr></thead>
                <tbody>
                  {v.map((r, i) => (
                    <tr key={i}>
                      <td className={classes.td}>{String(r.created_at || "").slice(11, 16)}</td>
                      <td className={classes.td}>{ISO_FLAG(r.iso)} {r.iso}</td>
                      <td className={classes.td} title={r.canal || ""}>{r.plan || "—"}</td>
                      <td className={`${classes.td} ${classes.num}`}>{r.simbolo} {r.price} <span className={classes.hint}>{r.coin}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </div>

        {/* El panel "Últimos eventos" se sacó: mostraba el nombre crudo del evento
            ("prices") y la ruta pelada, o sea la misma cosa que la cinta pero sin
            traducir a algo que se pueda leer. La cinta lo reemplaza entero. */}
      </div>

      {/* Pie: cómo se está midiendo. Estaba arriba como banner y molestaba; acá queda
          disponible cuando uno se pregunta de dónde sale el número. */}
      <div style={{ marginTop: 16, paddingTop: 10, borderTop: "1px solid #E2E8F0", fontSize: 11.5, color: "#94A3B8", lineHeight: 1.6 }}>
        {salud.eventos_con_sesion === 0 ? (
          <>
            Todavía no llegan eventos con sesión desde el sitio, así que los visitantes se están midiendo
            por <b>IP distinta</b> — aproximado: varias personas en la misma red cuentan como una. Por lo
            mismo, en la cinta la navegación sólo puede decir <b>“entró al sitio”</b> con país y dispositivo:
            la ruta, la campaña y los pasos del embudo los manda el sitio, y eso arranca cuando se deploye
            la instrumentación del front.
          </>
        ) : (
          <>Visitantes medidos por sesión del navegador{t.medida_visitante === "mixta" ? ", con IP como respaldo en los eventos que graba el servidor" : ""}.</>
        )}
        {" "}Las consultas cuentan conversaciones (no mensajes) y excluyen spam.{" "}
        <b>Alumno</b> quiere decir que desde esa sesión o IP hubo una llamada autenticada en la ventana
        (el sitio pide los datos del alumno con su token en cada carga); quien navega deslogueado cuenta
        como sin cuenta, y si dos personas comparten IP y una es alumna, las dos caen del lado alumno.
        {audiencia !== "todos" && (
          <> El filtro de audiencia alcanza a <b>visitantes, países, mapa, campañas, dispositivos, pulso
          y la cinta</b>; ventas, consultas e intentos de pago se muestran completos, porque salen del
          checkout y del inbox y ahí no hay forma de saber si era alumno.</>
        )}
        {" "}Ventana actual: {data && data.window ? `${data.window.from} → ${data.window.to}` : "—"}.
      </div>
    </div>
  );
}
